import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const JoinByCodeDialog = ({ open, onOpenChange, onJoined }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (clean.length < 4) { toast.error("Enter a valid code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/groups/join-by-code", { code: clean });
      if (data.already_member) toast.info(`You're already in "${data.name}"`);
      else toast.success(`Joined ${data.name}`);
      onJoined?.(data.group_id);
      onOpenChange(false);
      setCode("");
      nav(`/groups/${data.group_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-sm" data-testid="join-by-code-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Join a group</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Invite code</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              maxLength={10}
              autoFocus
              placeholder="ABCD12"
              className="mt-1.5 bg-muted border-border h-12 text-center text-2xl font-mono tracking-[0.4em]"
              data-testid="join-code-input"
              inputMode="text"
              autoCapitalize="characters"
            />
            <p className="text-[11px] text-muted-foreground mt-2">Ask the group creator for the 6-character code.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} className="w-full brand-bg text-black hover:opacity-90 h-11" data-testid="join-code-submit">
              <LogIn className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              {loading ? "Joining…" : "Join group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
